<template>
  <div style="margin-bottom: 100px">
    <div class="d-flex justify-content-between">
      <h5>Daftar Kendaraan</h5>
      <div class="d-flex">
        <McButton
          v-if="contractId && selectedIdsLength"
          variant="secondary-red"
          class="mx-2 ms-auto"
          icon="icon-outline_trash"
          :text="$t('global.delete')"
          :disabled="globalDisabled"
          @click="$refs['mc-modal-delete'].show()"
        />
        <McButton
          v-if="contractId && ['Draf', 'Dikonfirmasi'].includes(customerContractStatusName)"
          variant="primary"
          :text="$t('global.addField')"
          icon="icon-fill_plus"
          :disabled="disabled || globalDisabled"
          @click="handleAdd"
        />
      </div>
    </div>

    <b-card class="mt-3">
      <template v-if="isCreatingContract">
        <div class="d-flex align-items-center justify-content-center gap-2">
          <img src="@/assets/images/no-data-illustration.png" alt width="120px" />
          <div>
            <strong>Daftar Kendaraan belum bisa ditambah</strong>
            <p class="mb-0 text-muted">Simpan Kontrak terlebih dahulu</p>
          </div>
        </div>
      </template>
      <template v-else>
        <b-row align-h="between" class="mb-3">
          <b-col md="8" xl="10">
            <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              Baris terpilih.
              <a class="color-primary" @click="handleUnselectAll"> Bersihkan pilihan </a>
            </h6>
          </b-col>
          <b-col md="4" xl="2">
            <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
              <template #prepend>
                <i class="ps-2 mc-icon icon-fill_search" />
              </template>
              <b-form-input
                ref="searchInput"
                placeholder="Cari..."
                :disabled="disabled || globalDisabled"
                :value="search"
                @input="handleSearchChange"
              />
              <template v-if="search" #append>
                <button class="clear-search-btn" :disabled="disabled" @click="handleClearSearch">
                  <i class="pe-2 mc-icon icon-outline_close"></i>
                </button>
              </template>
            </b-input-group>
          </b-col>
        </b-row>

        <FormulateForm
          ref="formRef"
          v-model="formValues"
          name="editable-table-form"
          :errors="formulateFormErrors"
          @submit="handleSubmit"
          @failed-validation="handleFailedValidation"
        >
          <div class="table-responsive border border-1">
            <b-table
              class="editable-table"
              :busy="isBusy"
              :items="items"
              :fields="computedFields"
              :per-page="limit"
              fixed
              show-empty
            >
              <template #table-busy>
                <div class="text-center">
                  <b-spinner class="mb-2" />
                  <div>
                    <strong>{{ $t('global.text.loading') }}...</strong>
                  </div>
                </div>
              </template>

              <template #empty>
                <div class="text-center my-4">{{ $t('global.text.noData') }}</div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <div class="form-check" style="width: 10px">
                  <b-form-checkbox
                    v-model="selectedAll"
                    :disabled="disabled || globalDisabled"
                    @change="handleSelectAll"
                  />
                </div>
              </template>

              <!-- Slot: Header Data -->
              <template #head()="data">
                <div class="d-flex justify-content-between" @click="handleSortChange(data)">
                  {{ data.label }}
                  <template v-if="data.field.isSortable">
                    <div>
                      <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                        <i class="mc-icon icon-outline_chevron-up"></i>
                      </div>
                      <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                        <i class="mc-icon icon-outline_chevron-down"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <div class="form-check">
                  <b-form-checkbox
                    :checked="selectedIds[data.item.id]"
                    :disabled="disabled || globalDisabled"
                    @change="(event) => handleSelect(event, data)"
                  />
                </div>
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ index, field, value, item }">
                <!-- Editable, <Edit action> -->
                <template v-if="field.isEditable && isEditingRow == index">
                  <FormulateInput
                    v-bind="{ ...field }"
                    :ref="`${field.name}-ref`"
                    :key="field.name || field.key"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :fullObject="fullObjects?.[field.name] ? fullObjects[field.name] : undefined"
                    :disabled="isSubmitting || field?.disabledFn?.()"
                    :help="field?.validation?.includes('required')"
                    :validation-name="field.label"
                    :class="{
                      'p-0': true,
                      'cell-error-field': hasCellError(field, item, index),
                    }"
                    validation-behavior="live"
                    error-behavior="value"
                    size="sm"
                    @update:fullObject="$set(fullObjects, field.name, $event)"
                  />
                  <template v-for="errorMessage in getCellErrorMessages(field, item, index)">
                    <div
                      :key="`${field.key}-${errorMessage}-edit`"
                      class="cell-error-message"
                    >
                      {{ errorMessage }}
                    </div>
                  </template>
                </template>

                <template v-else-if="field.customColumn && isEditingRow !== index">
                  <slot :name="field.key" v-bind="{ index, field, value, item }"></slot>
                </template>
                <template v-else-if="field.readOnly">
                  <slot :name="`${field.key}`" v-bind="{ index, field, value, item }"></slot>
                </template>
                <!-- Not Editable, Show Only Column, <Edit action> -->
                <template v-else-if="!field.isEditable && isEditingRow === index">
                  <div>{{ showOnlyValue(value) }}</div>
                </template>
                <template v-else>
                  <div>{{ transformCellValue(value, field) }}</div>
                  <template v-for="errorMessage in getCellErrorMessages(field, item, index)">
                    <div :key="`${field.key}-${errorMessage}`" class="cell-error-message">
                      {{ errorMessage }}
                    </div>
                  </template>
                </template>
              </template>

              <!-- Slot: Header Action -->
              <template #head(action)>
                <div>Aksi</div>
              </template>

              <!-- Slot: Action Column (Last Column) -->
              <template #cell(action)="data">
                <div
                  class="__action-wrapper"
                  :style="`${
                    ['Draf', 'Dikonfirmasi'].includes(customerContractStatusName)
                      ? 'min-width: 210px;'
                      : 'min-width: 140px;'
                  }`"
                >
                  <McButton
                    class="history-btn"
                    variant="secondary"
                    text="Lihat Riwayat"
                    icon="icon-outline_Sync"
                    :disabled="isAdding || disabled || globalDisabled"
                    @click="handleShowHistoryModal(data.item?.id)"
                  />
                  <div
                    v-if="['Draf', 'Dikonfirmasi'].includes(customerContractStatusName)"
                    class="d-inline-block ms-2"
                  >
                    <div v-if="isEditingRow == data.index">
                      <b-button
                        variant="outline"
                        class="__cancel-button btn p-1"
                        @click="handleEditCancel(data)"
                      >
                        <i class="mc-icon icon-outline_close"></i>
                      </b-button>

                      <b-button variant="outline" class="__accept-button btn p-1" type="submit">
                        <i class="mc-icon icon-fill_checkmark"></i>
                      </b-button>
                    </div>

                    <b-button
                      v-else
                      variant="outline"
                      :class="{
                        '__edit-button btn p-1': true,
                        __disabled: disabled || globalDisabled || customDisabledFn(data.item),
                      }"
                      @click="handleEditStart(data)"
                    >
                      <i class="mc-icon icon-outline_edit"></i>
                    </b-button>
                  </div>
                </div>
              </template>
            </b-table>
          </div>
        </FormulateForm>
        <div class="d-flex flex-wrap flex-md-nowrap">
          <div class="mc-limit">
            <div class="__limit">
              <div>{{ $t('global.text.display') }}</div>
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="disabled || globalDisabled"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              />
              <div>{{ $t('global.text.data') }}</div>
            </div>

            <div v-if="totalCount === 0" class="__info">
              <strong>{{ totalCount }}</strong> {{ $t('global.object.of') }}
              <strong>{{ totalCount }}</strong> {{ $t('global.object.data') }}
            </div>

            <div v-else class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
              {{ $t('global.text.data') }}
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="disabled || globalDisabled"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            />
          </div>
        </div>
      </template>

      <b-modal
        id="mc-modal-add"
        ref="mc-modal-add"
        class="mc-modal"
        title="Simpan Data"
        size="md"
        centered
        scrollable
      >
        <div class="card-body">
          <McAlert withIcon class="w-100 mb-3" variant="warning">
            <template #label>
              <span><b>PERHATIAN!</b> Data yang telah disimpan tidak dapat dihapus kembali</span>
            </template>
          </McAlert>
          <p>Apakah Anda yakin ingin menambahkan dan menyimpan data baru?</p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.cancel')"
              @click="cancel()"
            />
            <McButton
              variant="primary"
              text="Simpan"
              @click="
                () => {
                  isAddConfirmed = true
                  $refs['mc-modal-add'].hide()
                  handleSubmit(submittedData)
                }
              "
            />
          </div>
        </template>
      </b-modal>

      <b-modal
        id="mc-modal-delete"
        ref="mc-modal-delete"
        class="mc-modal"
        title="Hapus"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            Apakah Anda yakin ingin menghapus <strong>{{ selectedIdsLength }}</strong> data berikut
            ?
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
          </div>
        </template>
      </b-modal>
    </b-card>

    <McEventList
      :value.sync="openHistory"
      :data="groupedHistories"
      :keys="groupedHistoryKeys"
      title="Riwayat Kendaraan Kontrak"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { debounce } from '@/helpers/debounce'
import { Helper } from '@/mixin/helper'
import { isEmpty } from 'lodash'
import moment from 'moment'

import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import McEventList from '@/components/McEventList'

import {
  transformCustomerContractVehicleUpdateRequest,
  transformCustomerContractVehicleShowRequest,
  transformCustomerContractVehicleStoreRequest,
} from '../lib/helpers/transform-data'

import fields from '../lib/fields/vehicle-list-fields'

import apiErrorMappings from '../lib/constants/api-error-mappings'
import apiErrorMappingsV2 from '../lib/constants/api-error-mappings-v2'
import vehicleCellErrorFilterMappings from '../lib/constants/vehicle-cell-error-filter-mappings'

import * as CustomerContract from '@/services/rest-api/lib/contract/customer-contract'

export default {
  components: {
    McButton,
    McAlert,
    McEventList,
  },
  mixins: [Helper, fields],
  props: {
    isCreatingContract: {
      type: Boolean,
      required: true,
    },
    globalDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const limitOptions = [10, 25, 50, 100]
    return {
      page: 1,
      currentPage: 1,
      limit: limitOptions[0] ?? 10,
      totalCount: 0,

      isBusy: true,
      isSubmitting: false,
      isAdding: false,
      isEditingRow: null,

      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,

      search: '',
      sorts: [],
      items: [],
      ids: [],

      showResponse: {},
      formValues: {},
      oldFormValues: {},
      initialFormValues: {},
      fullObjects: {},

      formulateFormErrors: {},

      limitOptions,
      trackBy: 'id',

      isAddConfirmed: false,
      submittedData: null,

      openHistory: false,
      groupedHistories: [],
      groupedHistoryKeys: [],
    }
  },
  computed: {
    ...mapState('masterCustomerContract', ['customerContract']),
    ...mapGetters('masterCustomerContract', ['customerContractStatusName']),
    disabled() {
      return this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    computedFields() {
      let fields = [
        this.customerContractStatusName === 'Draf'
          ? {
              key: 'checkbox',
              label: '',
              thStyle: 'width: 25px',
            }
          : {},
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
          tdAttr: {
            style:
              'right:0;position:sticky;z-index:100;border-left:1px solid #e6e7ec;vertical-align:middle;',
          },
          thAttr: {
            style:
              'right:0;position:sticky;z-index:100;border-left:1px solid #e6e7ec;vertical-align:middle;',
          },
        },
      ]

      return fields
    },
    // Customize Api CLient
    apiClient() {
      return {
        get: async (filter) => {
          let result = {
            metadata: {
              count: 0,
              page: 1,
              total_count: 0,
              total_page: 1,
            },
            data: {
              paginated_result: [],
            },
          }

          try {
            const res = await CustomerContract.getListVehicle(this.contractId, filter)

            if (res.errors) {
              this.$mcToast.error('Terjadi kesalahan.')
            } else {
              result = res
            }
          } catch (e) {
            console.log(e)
          }
          return result
        },
        show: CustomerContract.showVehicle,
        store: async (data) => {
          try {
            const transformedData = transformCustomerContractVehicleStoreRequest(data)

            const res = await CustomerContract.storeVehicle(this.contractId, transformedData)

            if (res.errors || res.errors_v2) {
              if (res.errors_v2) {
                this.formulateFormErrors = this.handleApiErrorMessageV2(res.errors_v2, res.errors)
              }

              return {
                errors: 'Kontrak kendaraan gagal diubah',
              }
            } else {
              this.formulateFormErrors = {}
            }

            return {
              message: 'success',
            }
          } catch (e) {
            console.error(e)
            return {
              errors: 'Kontrak kendaraan gagal diubah',
            }
          }
        },
        update: async (vehicleId, oldData, newData) => {
          try {
            const transformedOldData = transformCustomerContractVehicleShowRequest(oldData)

            const changedData = transformCustomerContractVehicleUpdateRequest(
              newData,
              transformedOldData
            )

            const res = await CustomerContract.updateVehicle(
              this.contractId,
              vehicleId,
              changedData
            )

            if (res.errors || res.errors_v2) {
              if (res.errors_v2) {
                this.formulateFormErrors = this.handleApiErrorMessageV2(res.errors_v2)
              }

              return {
                errors: 'Kontrak kendaraan gagal diubah',
              }
            } else {
              this.formulateFormErrors = {}
            }

            return {
              message: 'success',
            }
          } catch (e) {
            console.error(e)
            return {
              errors: 'Kontrak kendaraan gagal diubah',
            }
          }
        },
        delete: async (selectedVehicleIds) => {
          try {
            const formattedData = {
              customer_contract_vehicle_ids: selectedVehicleIds,
            }

            const res = await CustomerContract.deleteVehicle(this.contractId, formattedData)

            if (res?.errors) {
              return {
                errors: 'Kontrak kendaraan gagal dihapus',
              }
            }

            return {
              message: 'success',
            }
          } catch (e) {
            console.error(e)
            return {
              errors: 'Kontrak kendaraan gagal dihapus',
            }
          }
        },
      }
    },
    contractId() {
      return this.$route.params.contractId
    },
    customDisabledFn() {
      return (item) => {
        return item?.status?.raw_type === 'RETURNED'
      }
    },
  },
  watch: {
    contractId: {
      async handler(newId, oldId) {
        if ((newId && oldId === undefined) || (newId && oldId !== newId)) {
          await this.getData({
            limit: this.limit,
            page: this.page,
            sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
          })
        }
      },
    },
    async formValues() {
      if (this.isEditingRow == null) return

      // get the field that is currently changing
      const [patchData, hasChanged] = this.filterUnchangedData(
        this.formValues,
        this.initialFormValues
      )

      if (!hasChanged) return

      const changedFields = Object.keys(patchData)
      changedFields.forEach((field) => {
        if (!this.formulateFormErrors[field]) return

        // preserve error until the field value actually changes from the error state
        if (this.formValues[field] === this.oldFormValues[field]) return

        // skip delete error because it is handled on each field manually
        if (['return_date'].includes(field)) return

        // delete the field in formulateFormErrors once value has changed
        this.$delete(this.formulateFormErrors, field)
      })
    },
    'formValues.vehicle_type_id': function (newValue, oldValue) {
      if (this.isEditingRow === null) return

      if (newValue !== oldValue) {
        this.$set(this.formValues, 'vehicle_id', null)
        this.$set(this.fullObjects, 'vehicle_id', null)

        if (this.hasDriverCompatibility) {
          this.$set(this.formValues, 'driver_user_id', null)
          this.$set(this.fullObjects, 'driver_user_id', null)
          this.$set(this.formValues, 'driver_user_2_id', null)
          this.$set(this.fullObjects, 'driver_user_2_id', null)
        }
      }
    },
    'formValues.vehicle_id': function (newValue, oldValue) {
      if (this.isEditingRow === null) return

      if (newValue !== oldValue) {
        this.$set(this.formValues, 'driver_user_id', null)
        this.$set(this.fullObjects, 'driver_user_id', null)
        this.$set(this.formValues, 'driver_user_2_id', null)
        this.$set(this.fullObjects, 'driver_user_2_id', null)
      }
    },
    'formValues.return_date': function(newValue, _) {
      if (newValue === null || newValue === undefined) {
        this.$delete(this.formulateFormErrors, 'return_date')
        return
      }

      if (moment(newValue).isValid() && moment(this.customerContract.effective_date).isValid()) {
        if (moment(newValue).isSameOrBefore(this.customerContract?.effective_date)) {
          this.$set(this.formulateFormErrors, 'return_date', 'Tanggal kembali harus lebih dari tanggal mulai kontrak');
        } else {
          this.$delete(this.formulateFormErrors, 'return_date')
        }
      }
    }
  },
  async mounted() {
    if (!this.isCreatingContract) {
      await this.getData({
        limit: this.limit,
        page: this.page,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    }
  },
  methods: {
    handleApiErrorMessageV2(formattedErrors, fallbackErrors) {
      const formErrorsObj = {}
      formattedErrors.issues.map((issue) => {
        if (issue?.path) {
          let key = issue.path.join('')
          if (key.includes('.id')) {
            key = key.replace('.id', '_id')
          }

          if (apiErrorMappingsV2?.[key]?.[issue.code]) {
            formErrorsObj[key] = apiErrorMappingsV2[key][issue.code]
          } else {
            formErrorsObj[key] = ' '
          }
        }
      })

      // Fallback error to v1 errors
      if (isEmpty(formErrorsObj) && fallbackErrors) {
        Object.keys(fallbackErrors).map((key) => {
          let formattedKey = key
          if (formattedKey.includes('.id')) {
            formattedKey = formattedKey.replace('.id', '_id')
          }

          const msgErrorKey = fallbackErrors[formattedKey][0]
          if (apiErrorMappings?.[formattedKey]?.[msgErrorKey]) {
            formErrorsObj[formattedKey] = apiErrorMappings[formattedKey][msgErrorKey]
          } else {
            formErrorsObj[formattedKey] = ' '
          }
        })
      }

      return formErrorsObj
    },
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    handleFailedValidation() {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    showOnlyValue(value) {
      return value || '-'
    },
    transformCellValue(value) {
      if (value === false) return `-`
      if (typeof value === 'number') return `${value.toLocaleString()}`
      return value
    },
    normalizeErrorMessage(errorMessage) {
      return `${errorMessage}`.trim().toLowerCase()
    },
    shouldHideCellErrorForEditedField(field, rowIndex) {
      if (this.isEditingRow !== rowIndex) return false
      if (!field?.name) return false

      return (
        this.normalizeEmpty(this.formValues?.[field.name]) !==
        this.normalizeEmpty(this.initialFormValues?.[field.name])
      )
    },
    getCellErrorMessages(field, item, rowIndex) {
      // Hide stale API row errors when user already changes field value while editing.
      if (this.shouldHideCellErrorForEditedField(field, rowIndex)) return []

      const fieldKey = field?.key
      const fieldFilter = vehicleCellErrorFilterMappings[fieldKey]
      if (!fieldFilter) return []

      const { objectKey: targetObjectKey, rules } = fieldFilter
      const targetObjectErrors = Array.isArray(item?.[targetObjectKey]?.errors)
        ? item[targetObjectKey].errors
        : []
      const itemErrors = Array.isArray(item?.errors) ? item.errors : []

      const normalizedErrors = [...targetObjectErrors, ...itemErrors].map((errorMessage) =>
        this.normalizeErrorMessage(errorMessage)
      )

      const uniqueErrors = [...new Set(normalizedErrors)]

      const mappedMessages = rules
        .filter((rule) =>
          rule.errorValues.some((errorValue) =>
            uniqueErrors.includes(this.normalizeErrorMessage(errorValue))
          )
        )
        .map((rule) => rule.message)

      return [...new Set(mappedMessages)]
    },
    hasCellError(field, item, rowIndex) {
      return this.getCellErrorMessages(field, item, rowIndex).length > 0
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    async getData(params) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      try {
        const data = await this.apiClient.get(params)

        if (!data || data?.errors) {
          this.isBusy = false
          return
        }

        this.items = data.data.paginated_result
        this.totalCount = data.metadata.total_count
        this.ids = data.data.ids
      } catch (e) {
        this.$mcToast.error('Terjadi kesalahan')
      } finally {
        this.isBusy = false
      }
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.$refs['searchInput'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.search = ''
      this.handleSearchChange(undefined)
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
    },
    async handleEditStart(data) {
      if (this.globalDisabled || this.disabled || this.customDisabledFn(data.item)) return
      if (this.selectedIdsLength) this.handleUnselectAll()

      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.show(this.contractId, data.item.id)
      if (!refetchedData || refetchedData.errors) return

      const vehiclesData = refetchedData.data

      // assign formValues with response show data
      this.fields.forEach((field) => {
        if (!field.isEditable) return
        let value
        let fullObject
        let key = field.key

        if (field.name === 'vehicle_type_id') {
          value = vehiclesData.vehicle_type.id
          fullObject = {
            id: vehiclesData.vehicle_type.id,
            type: vehiclesData.vehicle_type.name,
            category: vehiclesData.vehicle_type.category_name,
          }

          key = field.name
        } else if (field.name === 'vehicle_id') {
          value = vehiclesData.vehicle.id
          fullObject = vehiclesData.vehicle

          key = field.name
        } else if (field.name === 'driver_user_id') {
          value = vehiclesData?.driver_user?.id
          fullObject = vehiclesData?.driver_user

          key = field.name
        } else if (field.name === 'driver_user_2_id') {
          value = vehiclesData?.driver_user_2?.id
          fullObject = vehiclesData?.driver_user_2

          key = field.name
        } else value = vehiclesData[field.key]

        this.$set(this.formValues, key, value)
        if (fullObject) {
          this.$set(this.fullObjects, key, fullObject)
        }
      })

      await this.$nextTick()

      this.isEditingRow = data.index
      // Keep original row snapshot (including errors) for cancel action.
      this.showResponse = { ...data.item }
      this.initialFormValues = { ...this.formValues }
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formulateFormErrors = {}
      this.fullObjects = {}

      this.isEditingRow = null
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)
      if (!createdData) return false

      if (createdData.errors) {
        this.$mcToast.error(createdData.errors)
        this.handleEditCancel({ index: this.isEditingRow })
        return false
      }

      await this.getData({
        limit: this.limit,
        page: this.page,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })

      // Emit event to notify parent about vehicle added
      this.$emit('vehicle-added')

      this.$mcToast.success('Kontrak kendaraan berhasil ditambahkan dan disimpan')
      return null
    },
    async submitUpdate(data, oldData) {
      const [patchData, hasChanged] = this.filterUnchangedData(data, this.initialFormValues)

      if (!hasChanged) {
        this.isSubmitting = false
        this.handleEditCancel({ index: this.isEditingRow })
        return
      }

      let detailData

      if (hasChanged) {
        let newData = patchData

        if (data[this.trackBy] !== oldData[this.trackBy]) newData = data

        const updatedData = await this.apiClient.update(oldData[this.trackBy], oldData, newData)

        if (!updatedData) return false

        const refetchedData = await this.apiClient.show(this.contractId, oldData[this.trackBy])
        if (!refetchedData) return false
        detailData = refetchedData.data

        if (updatedData.errors) {
          this.$mcToast.error(updatedData.errors)
          this.handleEditCancel({ index: this.isEditingRow })
          return false
        }
      }

      this.formulateFormErrors = {}

      this.$mcToast.success('Kontrak kendaraan berhasil diubah dan pelacakan diperbarui')

      return {
        ...detailData,
      }
    },
    async handleSubmit(data) {
      if (this.isAdding && this.customerContractStatusName === 'Dikonfirmasi' && !this.isAddConfirmed) {
        this.submittedData = data
        this.$refs['mc-modal-add'].show()
        return
      }
      this.submittedData = null
      this.isAddConfirmed = false

      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (Object.keys(this.formulateFormErrors).length) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.fields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }
      // If newData is false, it means there is an error
      
      if (newData === false) {
        this.isSubmitting = false
        this.oldFormValues = { ...this.formValues }
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.fullObjects = {}
      this.oldFormValues = {}
      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },
    normalizeEmpty(value) {
      // Treat 0, '', null, and undefined as the same (empty)
      if (value === 0 || value === '' || value === null || value === undefined) {
        return 'EMPTY'
      }
      return value
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        // Compare normalized values to treat 0/''/null/undefined as equivalent
        if (this.normalizeEmpty(newData[key]) === this.normalizeEmpty(oldData[key])) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const filteredSelectedIds = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          filteredSelectedIds.push(key)
        }
      }
      const deletedData = await this.apiClient.delete(filteredSelectedIds)

      if (!deletedData) return false

      if (deletedData.errors) {
        this.$refs['mc-modal-delete'].hide()
        this.$mcToast.error(deletedData.errors)
        return
      }

      if (this.search) this.handleClearSearch()
      await this.getData({
        limit: this.limit,
        page: this.page,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })

      await this.$nextTick()
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Kontrak kendaraan berhasil dihapus')
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.items = [{ id: 0 }, ...this.items]
    },
    async handleShowHistoryModal(vehicleId) {
      this.openHistory = true
      await this.fetchVehicleContractHistory(vehicleId)
    },
    async fetchVehicleContractHistory(vehicleId) {
      try {
        const res = await CustomerContract.getCustomerContractVehicleHistory(
          this.contractId,
          vehicleId
        )

        let historyData = res.data
        historyData = historyData.map((data) => {
          const date = new Date(data.created_on)

          const options = { day: '2-digit', month: 'short', year: 'numeric' }
          const formattedDate = date.toLocaleDateString('id-ID', options)

          data.date = formattedDate
          return data
        })
        historyData = Object.groupBy(historyData, ({ date }) => date)
        this.groupedHistoryKeys = Object.keys(historyData)
        this.groupedHistories = Object.keys(historyData).map((date) => ({
          [date]: historyData[date],
        }))
      } catch (e) {
        this.$mcToast.error('Telah terjadi kesalahan')
        this.groupedHistories = []
        this.groupedHistoryKeys = []
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.editable-table :deep(td) {
  vertical-align: middle;
}

.cell-error-message {
  color: $danger;
  margin-top: 4px;
}

.cell-error-field {
  :deep(.formulate-input-wrapper .formulate-input-element > input),
  :deep(.formulate-input-wrapper .formulate-input-element--textarea > textarea),
  :deep(.formulate-input-wrapper .formulate-input-element--time-duration > .mc-input),
  :deep(.formulate-input-wrapper .formulate-input-element--value-help > .mc-input),
  :deep(.formulate-input-wrapper .formulate-input-element--value-help-v2 > .mc-input),
  :deep(.formulate-input-wrapper .formulate-input-element--route-value-help > .mc-input),
  :deep(.formulate-input-wrapper .formulate-input-element--vendor-value-help > .mc-input) {
    border-color: $danger !important;
  }

  :deep(.formulate-input-wrapper .formulate-input-element > .mx-datepicker input[name='date']) {
    border-color: $danger !important;
  }

  :deep(.formulate-input-wrapper .multiselect__tags),
  :deep(.formulate-input-wrapper .multiselect--active .multiselect__tags) {
    border-color: $danger !important;
  }
}

.mc-has-search {
  border-width: $input-border-width;
  border-color: $input-border-color;
  border-style: solid;
  border-radius: 4px;

  // if search is disabled
  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    border-radius: 4px;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon:before {
      font-size: 16px;
      color: $input-placeholder-color;
    }
  }

  .editable-table ul[class='formulate-input-errors'] li[class='formulate-input-error']::before {
    color: red;
    font-weight: 600;
    display: inline-block;
  }
}

.mc-limit {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 0px 8px 0px;
  width: 100%;
  color: $mep-color-text-400;

  @media screen and (max-width: 468px) {
    justify-content: center;
  }

  .__limit {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    color: $mep-color-text;

    .__limit--dropdown {
      width: fit-content;
    }
  }

  .__info {
    flex-shrink: 0;
    color: $mep-color-text;
  }
}

.mc-pagination-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 0px 8px 0px;
  width: 100%;

  // remove default margin from pagination
  ul[role='menubar'] {
    margin-bottom: 0;
  }

  @media screen and (max-width: 468px) {
    justify-content: center;
  }

  :deep(.pagination) {
    --bs-pagination-border-width: 0;
    --bs-pagination-border-radius: 100%;

    .page-link {
      padding: 4px;
      min-height: 24px;
      aspect-ratio: 1 / 1;
      border-radius: 100%;
      font-size: 12px;
    }

    span.page-link {
      text-align: center;
    }

    .page-link.active,
    .active > .page-link {
      background-color: $mep-primary-400;
    }

    .page-item {
      &:nth-child(1),
      &:nth-child(2),
      &:nth-last-child(1),
      &:nth-last-child(2) {
        .page-link {
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 16px;
          padding-bottom: 10%;
        }
      }
    }
  }
}

.__action-wrapper {
  display: flex;
  align-items: center;
}

.__cancel-button .mc-icon:before {
  color: $danger;
}

.__accept-button .mc-icon:before {
  color: $success;
}

.__disabled .mc-icon:before {
  opacity: $btn-disabled-opacity;
  cursor: not-allowed;
}

::v-deep .history-btn .mc-button {
  padding: 4px 8px 4px 8px !important;
}
</style>
